import { FiltersState } from 'components/map/ZoneFilterModal';
import {
  ParkZoneMemberGroup,
  ParkZoneWithVisibility,
} from 'components/park/ParkingZoneSearch';
import { compareDesc, isAfter, isBefore } from 'date-fns';
import { ParkingTypeFilter } from 'hooks/useParkingHistoryFilter';
import i18n from 'i18next';
import { Dictionary, groupBy } from 'lodash';
import { QuickAccessType } from 'navigation/ParkScreenQuickAccess';
import {
  favorites,
  recentParkingsWithSegment,
  recentZonesOrChargers,
} from 'states/persistInStorage';
import { LatLng } from 'types/MapTypes';
import {
  ChargingSession,
  CompanyBenefit,
  CountryCode,
  LocationType,
  Maybe,
  ParkingSession,
  ParkZone,
  ParkZoneNotes,
  PmcVisibility,
  PmcVisibilityProps,
  PricingTarget,
  Query,
  QueryReadParkingSessionsArgs,
  QueryReadReceiptsByTypeArgs,
  Receipt,
  ReceiptItemType,
  UnifiedPermit,
} from 'types/generatedSchemaTypes';
import { ParkingTariffAndZone } from 'types/states';
import { getItemFromStorage, updateStateAndStorage } from './MMKVStorageUtils';
import { countryCodeToLang, getLang } from './commonUtils';
import { getDistanceFromLatLonInMeters, makePoint } from './mapUtils';
import { isPermitForParkingZone } from './permitUtils';

export const removeFavorite = (uid: string) => {
  const favoritesString = getItemFromStorage('favorites') || '';
  const stringToArray = favoritesString?.split(',');
  const index = stringToArray.indexOf(uid);
  if (index > -1) {
    stringToArray.splice(index, 1);
  }
  const newString = stringToArray.join();
  updateStateAndStorage(favorites, 'favorites', newString);
};

export const addFavorite = (uid: string) => {
  const favoritesString = getItemFromStorage('favorites') || '';
  const stringToArray = favoritesString?.split(',');
  const index = stringToArray.indexOf(uid);
  // push only if it's not already there
  if (index === -1) {
    stringToArray.push(uid);
    const newString = stringToArray.join();
    updateStateAndStorage(favorites, 'favorites', newString);
  }
};

const MAX_RECENT_ZONES_OR_CHARGERS = 5;

export const addToRecentZonesOrChargers = (parkZone: ParkZone) => {
  const recentZonesOrChargersStored =
    getItemFromStorage('recentZonesOrChargers') || [];
  // Add to first item and remove duplicates or excessive items
  const newArray = [
    parkZone,
    ...recentZonesOrChargersStored.filter(
      (o: ParkZone) => o && o.uid !== parkZone.uid
    ),
  ];
  newArray.length = Math.min(newArray.length, MAX_RECENT_ZONES_OR_CHARGERS);
  recentZonesOrChargers(newArray);
};

export const removeFromRecentZonesOrChargers = (parkZone: ParkZone) => {
  const recentZonesOrChargersStored: ParkZone[] =
    getItemFromStorage('recentZonesOrChargers') || [];
  const newArray = recentZonesOrChargersStored.filter(
    (o) => o.uid !== parkZone.uid
  );
  recentZonesOrChargers(newArray);
};

export const getParkingZoneType = (locationType: LocationType) => {
  if (locationType) {
    return i18n.t(`parkingZone.locationType.${locationType}`);
  }
  return '';
};

// get location icon name
export const locationIcon: { [key: string]: string } = {
  garage: 'garage',
  default: 'Parking',
  anpr: 'CCTV-Outlined',
  evCharging: 'Charging',
  free: 'Free',
  skistar: 'Skistar',
  boat: 'Boat',
};

// return icon type based on parking zone location type and anpr
export const getLocationIconType = (
  locationType: LocationType | undefined | null,
  isANPR: Maybe<boolean> | undefined,
  isCharging: Maybe<boolean> | undefined = false
) => {
  if (isCharging) {
    return 'evCharging';
  }
  if (isANPR) {
    return 'anpr';
  }
  switch (locationType) {
    case 'HALL':
      return 'garage';
    default:
      return 'default';
  }
};

export const sortReceiptsByStartTime = (receipt: Receipt[]) =>
  receipt
    .slice() // Because aTeemPTo ASsigN rEadoNLy vALue!!!!
    .sort((a, b) =>
      compareDesc(
        new Date(a?.items?.[0]?.startTime || a.created || ''),
        new Date(b?.items?.[0]?.startTime || b.created || '')
      )
    );

export const updateReceiptQuery = (
  prev: Query,
  {
    fetchMoreResult,
  }: {
    fetchMoreResult?: Query;
    variables?: QueryReadReceiptsByTypeArgs;
  }
) => {
  if (!fetchMoreResult) {
    return prev;
  }
  const newObject: Query = {
    ...fetchMoreResult,
  };

  if (newObject?.readReceiptsByType?.content) {
    const oldItems = prev?.readReceiptsByType?.content || [];
    const newItems = fetchMoreResult?.readReceiptsByType?.content || [];
    newObject.readReceiptsByType.content = [...oldItems, ...newItems];
  }
  return newObject;
};

export const parkingTypes: ReceiptItemType[] = [
  'PARKING',
  'START_STOP',
  'ANPR',
];
export const chargingTypes: ReceiptItemType[] = [
  'EV_CHARGING_V2',
  'EV_CHARGING_ENERGY',
  'EV_CHARGING_TIME',
];
export const permitTypes: ReceiptItemType[] = [
  'PERMIT',
  'LONGTERM_PERMIT',
  'SALES_ORDER',
];
export const prepaidTypes: ReceiptItemType[] = [
  'MONTHLY',
  'THIRTY_DAY',
  'PREPAID',
];

export const sortFilter = 'created,desc';

export const filterReceiptData = (
  receipts: Receipt[],
  filter?: ParkingTypeFilter
) => {
  if (receipts) {
    return receipts.filter((receipt) => {
      const type = (receipt?.items?.[0]?.type || '') as ReceiptItemType;

      switch (filter) {
        case 'parking':
          return parkingTypes.includes(type);
        case 'charging':
          return chargingTypes.includes(type);
        case 'permits':
          return permitTypes.includes(type);
        default:
          return true;
      }
    });
  }
};

export const filterParkingOrChargingSessions = (
  sessions?: ParkingSession[] & ChargingSession[],
  filter?: ParkingTypeFilter
) => {
  if (!sessions) {
    return [];
  }

  return sessions.filter((session) => {
    const sessionType = session.__typename;

    if (filter === 'parking') {
      return sessionType === 'ParkingSession';
    }

    if (filter === 'charging') {
      return sessionType !== 'ParkingSession';
    }

    return true;
  });
};

export const updateParkingQuery = (
  prev: Query,
  {
    fetchMoreResult,
  }: {
    fetchMoreResult?: Query;
    variables?: QueryReadParkingSessionsArgs;
  }
) => {
  if (!fetchMoreResult) {
    return prev;
  }
  const newObject: Query = {
    ...fetchMoreResult,
  };

  if (newObject?.readParkingSessions?.content) {
    const oldItems = prev?.readParkingSessions?.content || [];
    const newItems = fetchMoreResult?.readParkingSessions?.content || [];
    newObject.readParkingSessions.content = [...oldItems, ...newItems];
  }
  return newObject;
};

export const updateParkZonesPaginationQuery = (
  prev: Query,
  fetchMoreResult?: Query
) => {
  if (!fetchMoreResult?.parkZonesV2?.content) {
    return prev;
  }
  return {
    ...prev,
    parkZonesV2: {
      ...prev.parkZonesV2,
      content: prev.parkZonesV2?.content
        ? prev.parkZonesV2.content.concat(fetchMoreResult.parkZonesV2.content)
        : fetchMoreResult.parkZonesV2.content,
    },
  };
};

export const getCustomerSegmentUid = (selectedCustomerSegment?: string) => {
  return selectedCustomerSegment !== 'Standard'
    ? selectedCustomerSegment
    : undefined;
};

export const isValidNote = (
  { priority, startDate, endDate, text, visibility }: ParkZoneNotes,
  countryCode: Maybe<CountryCode> | undefined,
  ...priorities: number[]
) => {
  const lang = getLang();
  const countryLang = countryCodeToLang(countryCode as CountryCode);
  const hasValidText = (lang && text[lang]) ?? text.en ?? text[countryLang];
  return (
    priorities.includes(priority) &&
    isAfter(new Date(), new Date(startDate)) &&
    (!endDate || isBefore(new Date(), new Date(endDate))) &&
    !!hasValidText &&
    visibility === 'AIMO_APP'
  );
};

export const quickMenuFilter = (
  parkZone: Maybe<ParkZone> | undefined,
  selectedQuickMenuItem: QuickAccessType | undefined
) => {
  if (selectedQuickMenuItem === 'CHARGING') {
    return parkZone?.parkingMethods?.evCharging;
  }
  if (selectedQuickMenuItem === 'PARKING') {
    return !parkZone?.parkingMethods?.unmanagedParking;
  }
  return true;
};

export const mapFilter = (
  parkZone: Maybe<ParkZone> | undefined,
  filters: FiltersState
) => {
  if (!parkZone) {
    return false;
  }

  const conditions = [
    filters.parkingAvailable
      ? !parkZone.parkingMethods?.unmanagedParking
      : true,
    filters.chargingAvailable ? parkZone.parkingMethods?.evCharging : true,
  ];

  return conditions.every(Boolean);
};

export const getNestedValue = (obj: any, key: string): any => {
  return key.split('.').reduce((acc, curr) => acc?.[curr], obj);
};

export const substringSearch = (
  term: string,
  items: Maybe<ParkZone>[],
  keys: string[]
): Maybe<ParkZone>[] => {
  const lowerTerm = term.toLowerCase();

  const filteredResults = items.filter((item) =>
    keys.some((key) => {
      const value = getNestedValue(item, key);
      return (
        typeof value === 'string' && value.toLowerCase().includes(lowerTerm)
      );
    })
  );

  return [...filteredResults].sort((a, b) => {
    const aIsExactMatch = keys.some(
      (key) => getNestedValue(a, key)?.toLowerCase() === lowerTerm
    );
    const bIsExactMatch = keys.some(
      (key) => getNestedValue(b, key)?.toLowerCase() === lowerTerm
    );

    if (aIsExactMatch && !bIsExactMatch) {
      return -1;
    }
    if (!aIsExactMatch && bIsExactMatch) {
      return 1;
    }

    return 0;
  });
};

export const distanceCompare = (
  a: Maybe<ParkZone> | undefined,
  b: Maybe<ParkZone> | undefined,
  currentLocation: LatLng | null
) => {
  if (!currentLocation) {
    return 0;
  }

  const coordinatesA = a?.address?.marker?.coordinates;
  const coordinatesB = b?.address?.marker?.coordinates;

  if (!coordinatesA && !coordinatesB) {
    return 0;
  }

  if (coordinatesA && coordinatesB) {
    const distanceA = getDistanceFromLatLonInMeters(
      currentLocation,
      makePoint(coordinatesA)
    );
    const distanceB = getDistanceFromLatLonInMeters(
      currentLocation,
      makePoint(coordinatesB)
    );
    return distanceA - distanceB;
  }

  return coordinatesA ? -1 : 1;
};

export const quickMenuSort = (
  a: Maybe<ParkZone> | undefined,
  b: Maybe<ParkZone> | undefined,
  selectedQuickMenuItem: QuickAccessType | undefined,
  currentLocation: LatLng | null,
  withLTP?: boolean
) => {
  const fieldCompare = (fieldA: boolean, fieldB: boolean) => {
    if (fieldA === fieldB) {
      return distanceCompare(a, b, currentLocation);
    } else if (fieldA) {
      return -1;
    }
    return 1;
  };

  if (selectedQuickMenuItem === 'CHARGING' && !withLTP) {
    return fieldCompare(
      a?.parkingMethods?.evCharging ?? false,
      b?.parkingMethods?.evCharging ?? false
    );
  } else if (selectedQuickMenuItem === 'PARKING' && !withLTP) {
    return fieldCompare(
      !a?.parkingMethods?.unmanagedParking,
      !b?.parkingMethods?.unmanagedParking
    );
  }
  return distanceCompare(a, b, currentLocation);
};
export const addRecentParkingWithSegment = (
  parkingWithSegment: ParkingTariffAndZone
) => {
  const recentParkingsWithSegmentArray =
    getItemFromStorage('recentParkingsWithSegment') || [];
  // Find if user has a parking made with a customer segment
  const foundIndex = recentParkingsWithSegmentArray.findIndex(
    (item: ParkingTariffAndZone) => item.zoneUid === parkingWithSegment.zoneUid
  );
  if (foundIndex !== -1) {
    // If a zoneCode is found, update that object in the array and push the new array to the storage
    recentParkingsWithSegmentArray[foundIndex].customerSegmentUid =
      parkingWithSegment.customerSegmentUid;
    recentParkingsWithSegmentArray[foundIndex].customerSegmentName =
      parkingWithSegment.customerSegmentName;
    recentParkingsWithSegment(recentParkingsWithSegmentArray);
    return;
  }
  // zone code not found, add the new parking to the array
  const newArray = [...recentParkingsWithSegmentArray, parkingWithSegment];
  recentParkingsWithSegment(newArray);
};

export const getRecentParkingWithSegment = (zoneUid: string) => {
  const recentParkingsWithSegmentArray =
    getItemFromStorage('recentParkingsWithSegment') || [];
  return recentParkingsWithSegmentArray.find(
    (parking: ParkingTariffAndZone) => parking.zoneUid === zoneUid
  );
};

const expandSalesLocationVisibilities = (
  visibilities: Maybe<PmcVisibilityProps[]> | undefined
): PmcVisibilityProps[] => {
  return (visibilities ?? []).flatMap((visibility) => {
    if (visibility.parkingZoneUid) {
      return [visibility];
    }
    const zones = visibility.salesLocation?.parkingZones ?? [];
    const uniqueZones = Array.from(
      new Set(zones.filter((zoneUid): zoneUid is string => Boolean(zoneUid)))
    );
    return uniqueZones.map((parkingZoneUid) => ({
      ...visibility,
      parkingZoneUid,
    }));
  });
};

/**
 * Retrieves an array of park zones with their visibility information based on household benefits and payment method.
 *
 * @param {ParkZone[]} parkZones - An array of park zones.
 * @param {PmcVisibilityProps[]} visibilities - An array of visibility properties.
 * @param {CompanyBenefit[]} benefits - An array of household benefits.
 * @returns {ParkZoneWithVisibility[]} An array of park zones with their visibility information.
 */
export const getZonesWithVisibility = (
  parkZones: ParkZone[],
  visibilities: Maybe<PmcVisibilityProps[]> | undefined,
  benefits: Maybe<CompanyBenefit[]> | undefined,
  currentLocation: LatLng | null,
  currentAppCountry: Maybe<CountryCode>,
  skipSorting = false
) => {
  const expandedVisibilities = expandSalesLocationVisibilities(visibilities);
  const groupByUid = groupBy(
    expandedVisibilities,
    (item) => item.parkingZoneUid
  );

  const filteredZones = parkZones
    ?.filter((parkZone) =>
      filterParkzonesByVisibilityLTP(parkZone, benefits, groupByUid)
    )
    .map((parkZone) => {
      return {
        ...parkZone,
        ...addMemberGroup(groupByUid[parkZone?.uid], benefits),
      } as ParkZoneWithVisibility;
    });

  if (skipSorting) {
    return filteredZones;
  }
  return sortZonesByHouseholdPermits(
    filteredZones,
    groupByUid,
    benefits,
    currentLocation,
    currentAppCountry
  );
};

const filterParkzonesByVisibilityLTP = (
  parkZone: ParkZone | undefined,
  benefits: Maybe<CompanyBenefit[]> | undefined,
  groupByUid: Dictionary<PmcVisibilityProps[]>
) => {
  const zoneVisibility = parkZone?.visibility as PmcVisibility;
  const parkZonePmcVisibilities = groupByUid[parkZone?.uid ?? ''];

  const hasAnyCreditCardPayment = parkZonePmcVisibilities?.some(
    (pmc) => pmc.hasCreditCardPayment
  );

  if (!hasAnyCreditCardPayment) {
    return false;
  }

  return (
    isPublicParkzone(zoneVisibility) ||
    isPrivateZone(zoneVisibility, parkZonePmcVisibilities, benefits) ||
    hasBenefitForZone(benefits, parkZone?.uid as string)
  );
};

export const parkMapParkzoneFilter = (
  parkZones: Maybe<ParkZone>[],
  benefits: Maybe<CompanyBenefit[]> | undefined,
  permits?: Maybe<UnifiedPermit[]> | undefined,
  filterUnmanaged: boolean = false
) => {
  return parkZones?.filter(
    (parkZone) =>
      filterParkzonesByVisibilityAndBenefit(parkZone, benefits, permits) &&
      (!filterUnmanaged || filterParkZonesByUnmanagedParking(parkZone))
  );
};

const filterParkzonesByVisibilityAndBenefit = (
  parkZone: Maybe<ParkZone> | undefined,
  benefits: Maybe<CompanyBenefit[]> | undefined,
  permits?: Maybe<UnifiedPermit[]> | undefined
) => {
  const zoneVisibility = parkZone?.visibility as PmcVisibility;
  const parkzoneUid = parkZone?.uid as string;
  return (
    isPublicParkzone(zoneVisibility) ||
    (zoneVisibility === 'PRIVATE' &&
      hasBenefitForZone(benefits, parkzoneUid)) ||
    permits?.some((permit) => isPermitForParkingZone(parkzoneUid, permit))
  );
};

/**
 *
 * If zone is unmanaged and
 * Marker toggle is off → Marker is not shown in map
 * If zone is unmanaged and
 * Marker toggle is on → Marker is show in map
 * If zone is managed and
 * Marker toggle is off → Marker is shown in map (default marker)
 */
const filterParkZonesByUnmanagedParking = (
  parkZone: Maybe<ParkZone> | undefined
) => {
  return (
    !parkZone?.parkingMethods?.unmanagedParking || Boolean(parkZone.markerType)
  );
};

export const hasBenefitForZone = (
  benefits: Maybe<CompanyBenefit[]> | undefined,
  parkZoneUid: string
) => {
  return benefits?.some(({ pmc }) => {
    const { physicalZoneUid, salesLocation } = pmc || {};
    return (
      physicalZoneUid === parkZoneUid ||
      salesLocation?.parkingZones?.includes(parkZoneUid)
    );
  });
};

export const sortInMainMapViewByZoneAndBenefit = (
  a: Maybe<ParkZone> | undefined,
  b: Maybe<ParkZone> | undefined,
  benefits: Maybe<CompanyBenefit[]> | undefined,
  currentLocation?: LatLng | null
) => {
  const zoneUidA = a?.uid as string;
  const zoneUidB = b?.uid as string;

  const hasBenefitForA = hasBenefitForZone(benefits, zoneUidA);
  const hasBenefitForB = hasBenefitForZone(benefits, zoneUidB);

  if (hasBenefitForA && !hasBenefitForB) {
    return -1;
  }
  if (!hasBenefitForA && hasBenefitForB) {
    return 1;
  }

  return currentLocation ? distanceCompare(a, b, currentLocation) : 0;
};

/*
  HELPER FUNCTIONS USED IN ABOVE FILTERING OF PARK ZONES
*/

// Check if zone is public. If visibility is NULL, treat it as public
const isPublicParkzone = (parkZoneVisibility: PmcVisibility | null) => {
  return parkZoneVisibility === 'PUBLIC' || parkZoneVisibility === null;
};

// Check if user has a benefit that matches one of the zones PMCs
const hasHouseholdBenefitForZoneLTP = (
  parkZonePmcs: PmcVisibilityProps[],
  userHouseholdbenefits: Maybe<CompanyBenefit[]> | undefined
) => {
  return parkZonePmcs?.some((pmc) => {
    return userHouseholdbenefits?.some(
      (benefit) => benefit.benefitTargetUid === pmc.pmcId
    );
  });
};

/*
Check if the zone is private. If it is, then require user to have a benefit for one of the private PMCs
OR that zone has to have atleast 1 public PMC to be visible in list, search & map
*/
const isPrivateZone = (
  parkZoneVisibility: PmcVisibility | null,
  parkZonePmcs: PmcVisibilityProps[],
  userHouseholdbenefits: Maybe<CompanyBenefit[]> | undefined
) => {
  return (
    parkZoneVisibility === 'PRIVATE' &&
    (hasHouseholdBenefitForZoneLTP(parkZonePmcs, userHouseholdbenefits) ||
      parkZonePmcs?.some((pmc) => pmc.visibility === 'PUBLIC'))
  );
};

// IF user has the membership to certain PMC add the benefits name to the search list item
const addMemberGroup = (
  pmcs: PmcVisibilityProps[],
  householdBenefits: Maybe<CompanyBenefit[]> | undefined
) => {
  return pmcs?.reduce((acc, obj) => {
    const householdBenefit = householdBenefits?.find(
      (benefit) => benefit.benefitTargetUid === obj.pmcId
    );
    if (householdBenefit && !acc.memberGroupName) {
      acc.memberGroupName = householdBenefit.name;
    }
    return acc;
  }, {} as ParkZoneMemberGroup);
};

const sortZonesByHouseholdPermits = (
  parkZones: ParkZone[],
  groupByUid: Dictionary<PmcVisibilityProps[]>,
  householdBenefits: Maybe<CompanyBenefit[]> | undefined,
  currentLocation: LatLng | null,
  currentAppCountry: Maybe<CountryCode>
) => {
  return [...parkZones].sort((a, b) => {
    const parkZonePmcVisibilitiesA = groupByUid[a?.uid ?? ''];
    const parkZonePmcVisibilitiesB = groupByUid[b?.uid ?? ''];
    const hasHhBenefitA = hasHouseholdBenefitForZoneLTP(
      parkZonePmcVisibilitiesA,
      householdBenefits
    );
    const hasHhBenefitB = hasHouseholdBenefitForZoneLTP(
      parkZonePmcVisibilitiesB,
      householdBenefits
    );

    const isHouseholdZoneA =
      hasHhBenefitA && a?.countryCode === currentAppCountry;
    const isHouseholdZoneB =
      hasHhBenefitB && b?.countryCode === currentAppCountry;

    if (isHouseholdZoneA !== isHouseholdZoneB) {
      return isHouseholdZoneA ? -1 : 1;
    }

    return distanceCompare(a, b, currentLocation);
  });
};

export const isItemParking = (
  item: ParkingSession | ChargingSession
): item is ParkingSession => {
  return (item as ParkingSession).sessionId !== undefined;
};

/**
 * Extracts and flattens all parking zone UIDs from pricing targets data
 * @param pricingTargets - Array of pricing targets from getPricingTargets query
 * @returns Array of unique parking zone UIDs
 */
export const extractParkingZoneUidsFromPricingTargets = (
  pricingTargets: Maybe<PricingTarget>[] | undefined
): string[] => {
  if (!pricingTargets || pricingTargets.length === 0) {
    return [];
  }
  const parkingZoneArrays = pricingTargets.flatMap(
    (target) => target?.parkingZoneUids ?? []
  );
  const filteredParkingZoneUids = parkingZoneArrays.filter(
    (uid) => uid !== null && uid !== undefined
  );
  return [...new Set(filteredParkingZoneUids)];
};
